/**
 * SchedulingWizardCore
 *
 * Core reutilizável dos três wizards de agendamento:
 *  - AgendarPage (público)
 *  - RemarcarPage (público)
 *  - WizardAgendamento (interno/atendente)
 *
 * Gerencia os passos: calendário → horários → confirmação.
 * A camada de API (pública vs autenticada) é injetada via props.
 */

import { useState, useEffect, useCallback, ReactNode } from 'react';
import { Check, Clock, Loader2, ChevronLeft } from 'lucide-react';
import { CalendarGrid } from './CalendarGrid';
import type { WizardAvailableDay, WizardAvailableSlot } from '@/services/appointment-wizard-service';

// ── Utilitário compartilhado ──────────────────────────────────────────────────

export function formatDateBR(dateStr: string): string {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

// ── Stepper (usado pelas páginas públicas) ────────────────────────────────────

const STEP_LABELS = ['Dia', 'Horário', 'Confirmar', 'Pronto'];

export type PublicStep = 'calendar' | 'slots' | 'confirm' | 'success';
const PUBLIC_STEP_N: Record<PublicStep, number> = { calendar: 1, slots: 2, confirm: 3, success: 4 };

export function SchedulingStepper({ current }: { current: PublicStep }) {
  const n = PUBLIC_STEP_N[current] ?? 1;
  return (
    <div className="flex items-center justify-center mb-5">
      {STEP_LABELS.map((label, i) => {
        const stepN = i + 1;
        const done = stepN < n;
        const active = stepN === n;
        return (
          <div key={label} className="flex items-center">
            <div className="flex flex-col items-center">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                done    ? 'bg-teal-600 text-white' :
                active  ? 'bg-teal-600 text-white ring-2 ring-teal-200' :
                          'bg-slate-200 text-slate-400'
              }`}>
                {done ? <Check className="h-3.5 w-3.5" /> : stepN}
              </div>
              <span className={`text-[9px] mt-0.5 font-semibold ${
                active ? 'text-teal-700' : done ? 'text-teal-500' : 'text-slate-400'
              }`}>{label}</span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div className={`w-8 h-0.5 mb-3.5 ${stepN < n ? 'bg-teal-500' : 'bg-slate-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── SchedulingWizardCore ──────────────────────────────────────────────────────

type CoreStep = 'calendar' | 'slots' | 'confirm';

export interface SchedulingWizardCoreProps {
  clinicId: string;
  specialtyId: string;
  specialtyName: string;
  doubleSlot: boolean;

  /** Função de busca de dias disponíveis (pública ou autenticada) */
  fetchDays: (
    clinicId: string, specialtyId: string, month: string, doubleSlot: boolean
  ) => Promise<WizardAvailableDay[]>;

  /** Função de busca de horários disponíveis (pública ou autenticada) */
  fetchSlots: (
    clinicId: string, specialtyId: string, date: string, doubleSlot: boolean
  ) => Promise<WizardAvailableSlot[]>;

  /** Chamado quando o usuário clica em confirmar. O pai executa a chamada de API. */
  onConfirm: (slot: WizardAvailableSlot) => Promise<void>;

  /** Loading externo do botão confirmar (enquanto o pai processa a confirmação) */
  confirming?: boolean;

  /** Exibe botão "Voltar" no calendário → permite retornar a step anterior do pai (ex: especialidade) */
  onBack?: () => void;
  backLabel?: string;

  /** Callback chamado quando o passo interno muda (para stepper externo ou logging) */
  onStepChange?: (step: CoreStep) => void;

  /** Callback de erro para o pai exibir (ex: tela de erro em páginas públicas) */
  onError?: (msg: string) => void;

  /** Conteúdo extra exibido no badge de duração (ex: "Consulta original: 01/05 às 14:00") */
  extraBadge?: ReactNode;

  /** Título do card de confirmação */
  confirmTitle?: string;

  /** Label do botão confirmar */
  confirmLabel?: string;
}

export function SchedulingWizardCore({
  clinicId,
  specialtyId,
  specialtyName,
  doubleSlot,
  fetchDays,
  fetchSlots,
  onConfirm,
  confirming = false,
  onBack,
  backLabel = 'Voltar',
  onStepChange,
  onError,
  extraBadge,
  confirmTitle = 'Resumo do Agendamento',
  confirmLabel = 'Confirmar Agendamento',
}: SchedulingWizardCoreProps) {
  const [step, setStep] = useState<CoreStep>('calendar');
  const [working, setWorking] = useState(false);
  const [viewDate, setViewDate] = useState(new Date());
  const [availability, setAvailability] = useState<WizardAvailableDay[]>([]);
  const [slots, setSlots] = useState<WizardAvailableSlot[]>([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState<WizardAvailableSlot | null>(null);

  const goTo = (s: CoreStep) => { setStep(s); onStepChange?.(s); };

  const loadAvailability = useCallback(async (month: string) => {
    setWorking(true);
    setAvailability([]);
    try {
      const data = await fetchDays(clinicId, specialtyId, month, doubleSlot);
      setAvailability(data);
    } catch {
      onError?.('Erro ao carregar dias disponíveis.');
    } finally {
      setWorking(false);
    }
  }, [clinicId, specialtyId, doubleSlot, fetchDays, onError]);

  useEffect(() => {
    if (clinicId && specialtyId) {
      loadAvailability(viewDate.toISOString().substring(0, 7));
    }
  }, [viewDate, clinicId, specialtyId, loadAvailability]);

  const handleSelectDay = async (date: string) => {
    setWorking(true);
    setSelectedDate(date);
    try {
      const data = await fetchSlots(clinicId, specialtyId, date, doubleSlot);
      setSlots(data);
      goTo('slots');
    } catch {
      onError?.('Erro ao carregar horários.');
    } finally {
      setWorking(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedSlot) return;
    await onConfirm(selectedSlot);
  };

  const busy = working || confirming;

  return (
    <div className="space-y-4">
      {/* Badge de duração + conteúdo extra */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-full bg-teal-100 text-teal-700">
          <Clock className="h-3 w-3" />
          {doubleSlot ? 'Consulta de 1h' : 'Consulta de 30min'}
        </span>
        {extraBadge}
      </div>

      {/* ── Calendário ── */}
      {step === 'calendar' && (
        <div className="space-y-3 relative">
          {working && (
            <div className="absolute inset-0 bg-white/60 z-20 flex items-center justify-center rounded-xl">
              <Loader2 className="h-7 w-7 animate-spin text-teal-600" />
            </div>
          )}
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Escolha um dia</p>
          <CalendarGrid
            availability={availability}
            onSelectDay={handleSelectDay}
            selectedDate={selectedDate}
            currentMonth={viewDate}
            onMonthChange={setViewDate}
          />
          {onBack && (
            <button
              onClick={onBack}
              disabled={busy}
              className="text-teal-600 text-xs font-bold hover:underline flex items-center gap-1 min-h-[36px]"
            >
              <ChevronLeft className="h-3 w-3" /> {backLabel}
            </button>
          )}
        </div>
      )}

      {/* ── Horários ── */}
      {step === 'slots' && (
        <div className="space-y-2">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
            Horários para {formatDateBR(selectedDate)}
          </p>
          <div className="grid grid-cols-1 gap-1 max-h-[280px] overflow-y-auto pr-1">
            {slots.map(slot => (
              <button
                key={slot.slot_id}
                onClick={() => { setSelectedSlot(slot); goTo('confirm'); }}
                className="flex justify-between items-center border border-slate-100 px-3 py-2 rounded-lg hover:border-teal-500 hover:bg-teal-50 group transition-all"
              >
                <div className="flex items-center gap-2 text-left">
                  <span className="text-sm font-black text-slate-700 group-hover:text-teal-700">
                    {slot.slot_time.substring(0, 5)} hs
                  </span>
                  {slot.double_slot && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                      <Clock className="h-2 w-2" /> 1h
                    </span>
                  )}
                  <span className="text-[11px] text-slate-400">— {slot.dentist_name}</span>
                </div>
                <Check className="h-4 w-4 text-slate-200 group-hover:text-teal-600" />
              </button>
            ))}
          </div>
          <button
            onClick={() => goTo('calendar')}
            className="text-teal-600 text-xs font-bold hover:underline flex items-center gap-1 min-h-[36px]"
          >
            <ChevronLeft className="h-3 w-3" /> Escolher outro dia
          </button>
        </div>
      )}

      {/* ── Confirmação ── */}
      {step === 'confirm' && selectedSlot && (
        <div className="space-y-3">
          <div className="bg-teal-600 rounded-xl p-4 text-white">
            <h4 className="font-bold text-sm mb-2 flex items-center gap-2">
              <Check className="h-4 w-4 bg-white text-teal-600 rounded-full p-0.5" />
              {confirmTitle}
            </h4>
            <div className="space-y-1 text-sm opacity-90">
              <p>Especialidade: <strong>{specialtyName}</strong></p>
              <p>Dentista: <strong>{selectedSlot.dentist_name}</strong></p>
              <p>Data: <strong>{formatDateBR(selectedDate)}</strong> às <strong>{selectedSlot.slot_time.substring(0, 5)} hs</strong></p>
              <p>Duração: <strong>{doubleSlot ? '1 hora' : '30 minutos'}</strong></p>
            </div>
          </div>
          <button
            onClick={handleConfirm}
            disabled={busy}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 rounded-xl shadow-md transition-colors disabled:opacity-60 flex items-center justify-center gap-2 min-h-[44px]"
          >
            {confirming && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </button>
          <button
            onClick={() => goTo('slots')}
            disabled={busy}
            className="w-full text-slate-400 hover:text-slate-600 text-sm font-semibold flex items-center justify-center gap-1 min-h-[36px]"
          >
            <ChevronLeft className="h-4 w-4" /> Alterar horário
          </button>
        </div>
      )}
    </div>
  );
}
